import React from "react";
import axios from "axios";
import moment from "moment";
import fileDownload from "js-file-download";
import {
    Link
} from "react-router-dom";
import {
    Alert,
    Button,
    Dropdown,
    OverlayTrigger,
    Spinner,
    Table,
    Tooltip
} from "react-bootstrap";
import {
    TabBar,
    Title
} from "@zandor300/backoffice-framework";

import ShopsContext from "../../context/ShopsContext";
import Loading from "../../components/Loading";
import TagPill from "../../components/pills/TagPill";
import Helmet from "../../components/Helmet";
import DateFormatter from "../../components/DateFormatter";

const DEFAULT_FILTERS = {
    inactiveForValue: "",
    inactiveForUnit: "months",
    lastCoursePurchaseAfter: "",
    lastCoursePurchaseBefore: "",
    lastUsedAfter: "",
    lastUsedBefore: ""
};

class ShopUsers extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            shopUsers: null,
            error: null,
            exportError: null,
            loading: false,
            exportLoading: false,
            filters: { ...DEFAULT_FILTERS },
            appliedFilters: { ...DEFAULT_FILTERS }
        }
        this.applyFilters = this.applyFilters.bind(this);
        this.clearFilters = this.clearFilters.bind(this);
        this.exportShopUsers = this.exportShopUsers.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
    }

    componentDidMount() {
        this.getShopUsers(this.state.appliedFilters);
    }

    getNormalizedFilters(filters) {
        const inactiveForValue = (filters.inactiveForValue ?? "").toString().trim();
        const entitlementCourses = this.context.currentShop.entitlementCourses;
        return {
            inactiveForValue,
            inactiveForUnit: inactiveForValue ? (filters.inactiveForUnit || "months") : "",
            lastCoursePurchaseAfter: entitlementCourses ? (filters.lastCoursePurchaseAfter || "") : "",
            lastCoursePurchaseBefore: entitlementCourses ? (filters.lastCoursePurchaseBefore || "") : "",
            lastUsedAfter: filters.lastUsedAfter || "",
            lastUsedBefore: filters.lastUsedBefore || ""
        };
    }

    hasActiveFilters(filters) {
        const normalizedFilters = this.getNormalizedFilters(filters);
        return Boolean(
            normalizedFilters.inactiveForValue ||
            normalizedFilters.lastCoursePurchaseAfter ||
            normalizedFilters.lastCoursePurchaseBefore ||
            normalizedFilters.lastUsedAfter ||
            normalizedFilters.lastUsedBefore
        );
    }

    getRequestPayload(filters) {
        const normalizedFilters = this.getNormalizedFilters(filters);
        const payload = {
            shopId: this.context.currentShop.id
        };

        if(normalizedFilters.inactiveForValue) {
            payload.inactiveForValue = normalizedFilters.inactiveForValue;
            payload.inactiveForUnit = normalizedFilters.inactiveForUnit;
        }
        if(normalizedFilters.lastCoursePurchaseAfter) {
            payload.lastCoursePurchaseAfter = normalizedFilters.lastCoursePurchaseAfter;
        }
        if(normalizedFilters.lastCoursePurchaseBefore) {
            payload.lastCoursePurchaseBefore = normalizedFilters.lastCoursePurchaseBefore;
        }
        if(normalizedFilters.lastUsedAfter) {
            payload.lastUsedAfter = normalizedFilters.lastUsedAfter;
        }
        if(normalizedFilters.lastUsedBefore) {
            payload.lastUsedBefore = normalizedFilters.lastUsedBefore;
        }

        return payload;
    }

    getShopUserName(shopUser) {
        const fullName = [shopUser.firstName, shopUser.lastName].filter(Boolean).join(" ").trim();
        return fullName || shopUser.name || "-";
    }

    getShopUsers(filters) {
        const normalizedFilters = this.getNormalizedFilters(filters);
        this.setState({
            shopUsers: null,
            error: null,
            exportError: null,
            loading: true,
            appliedFilters: normalizedFilters
        });
        axios.post("/getShopUsers", this.getRequestPayload(normalizedFilters))
            .then((response) => {
                if(response.data.valid) {
                    const shopUsers = response.data.shopUsers.sort((shopUser1, shopUser2) => {
                        return this.getShopUserName(shopUser1).localeCompare(this.getShopUserName(shopUser2), "nl", { sensitivity: "base" });
                    });
                    this.setState({ shopUsers, loading: false });
                } else {
                    this.setState({
                        error: "Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")",
                        loading: false
                    });
                }
            })
            .catch(() => {
                this.setState({
                    error: "Er ging iets fout. Probeer het later opnieuw.",
                    loading: false
                });
            });
    }

    handleFilterChange(event) {
        const {
            name,
            value
        } = event.target;
        this.setState(({ filters }) => ({
            filters: {
                ...filters,
                [name]: value
            }
        }));
    }

    applyFilters(event) {
        if(event) {
            event.preventDefault();
        }
        this.getShopUsers(this.state.filters);
    }

    applyFilterPatch(filterPatch) {
        const filters = {
            ...this.state.filters,
            ...filterPatch
        };
        this.setState({ filters }, () => {
            this.getShopUsers(filters);
        });
    }

    clearFilterGroup(filterKeys) {
        const filters = {
            ...this.state.filters
        };
        filterKeys.forEach((filterKey) => {
            filters[filterKey] = DEFAULT_FILTERS[filterKey];
        });
        this.setState({ filters }, () => {
            this.getShopUsers(filters);
        });
    }

    clearFilters() {
        this.setState({ filters: { ...DEFAULT_FILTERS } }, () => {
            this.getShopUsers(DEFAULT_FILTERS);
        });
    }

    exportShopUsers() {
        const shop = this.context.currentShop;
        this.setState({ exportLoading: true, exportError: null });
        axios.post("/exportShopUsersCSV", this.getRequestPayload(this.state.appliedFilters))
            .then((response) => {
                const safeShopName = shop.name
                    .replace(/[\\/:*?"<>|]/g, " ")
                    .replace(/\s+/g, " ")
                    .trim();
                fileDownload(response.data, safeShopName + " Inactieve Accounts Export " + moment().format("YYYY-MM-DD HH-mm-ss") + ".csv");
            })
            .catch(() => {
                this.setState({ exportError: "Er ging iets fout bij het exporteren. Probeer het later opnieuw." });
            })
            .finally(() => {
                this.setState({ exportLoading: false });
            });
    }

    hasPendingFilterChanges() {
        const filters = this.getNormalizedFilters(this.state.filters);
        const appliedFilters = this.getNormalizedFilters(this.state.appliedFilters);
        return Object.keys(filters).some((key) => filters[key] !== appliedFilters[key]);
    }

    hasAppliedFilterValues(filterKeys) {
        const appliedFilters = this.getNormalizedFilters(this.state.appliedFilters);
        return filterKeys.some((filterKey) => Boolean(appliedFilters[filterKey]));
    }

    getAppliedFilterCount(filterKeys) {
        const appliedFilters = this.getNormalizedFilters(this.state.appliedFilters);
        return filterKeys.filter((filterKey) => Boolean(appliedFilters[filterKey])).length;
    }

    formatSummaryDate(date) {
        if(!date || !moment(date).isValid()) {
            return null;
        }
        return moment(date).format("D MMMM YYYY");
    }

    getInactiveSummaryLabel(inactiveForValue, inactiveForUnit) {
        if(!inactiveForValue) {
            return null;
        }
        const unitLabel = inactiveForUnit === "days"
            ? (inactiveForValue === "1" ? "dag" : "dagen")
            : (inactiveForValue === "1" ? "maand" : "maanden");
        return "langer dan " + inactiveForValue + " " + unitLabel + " inactief";
    }

    getInactiveToggleLabel() {
        const {
            inactiveForValue,
            inactiveForUnit
        } = this.getNormalizedFilters(this.state.appliedFilters);
        if(!inactiveForValue) {
            return "Inactief langer dan";
        }
        const unitLabel = inactiveForUnit === "days"
            ? (inactiveForValue === "1" ? "dag" : "dagen")
            : (inactiveForValue === "1" ? "maand" : "maanden");
        return "Inactief > " + inactiveForValue + " " + unitLabel;
    }

    getRangeToggleLabel(title, filterKeys) {
        const selectedCount = this.getAppliedFilterCount(filterKeys);
        if(selectedCount === 0) {
            return title;
        }
        return title + " (" + selectedCount + ")";
    }

    getAppliedFilterSummary() {
        const {
            shopUsers,
            appliedFilters
        } = this.state;
        const normalizedFilters = this.getNormalizedFilters(appliedFilters);
        const entitlementCourses = this.context.currentShop.entitlementCourses;
        const summary = [((shopUsers?.length) ?? 0) + " account" + (((shopUsers?.length) ?? 0) === 1 ? "" : "s")];

        const inactiveSummary = this.getInactiveSummaryLabel(normalizedFilters.inactiveForValue, normalizedFilters.inactiveForUnit);
        if(inactiveSummary) {
            summary.push(inactiveSummary);
        }

        if(entitlementCourses) {
            const lastCoursePurchaseAfter = this.formatSummaryDate(normalizedFilters.lastCoursePurchaseAfter);
            const lastCoursePurchaseBefore = this.formatSummaryDate(normalizedFilters.lastCoursePurchaseBefore);
            if(lastCoursePurchaseAfter && lastCoursePurchaseBefore) {
                summary.push("laatste cursusaankoop tussen " + lastCoursePurchaseAfter + " en " + lastCoursePurchaseBefore);
            } else if(lastCoursePurchaseAfter) {
                summary.push("laatste cursusaankoop vanaf " + lastCoursePurchaseAfter);
            } else if(lastCoursePurchaseBefore) {
                summary.push("laatste cursusaankoop voor " + lastCoursePurchaseBefore);
            }
        }

        const lastUsedAfter = this.formatSummaryDate(normalizedFilters.lastUsedAfter);
        const lastUsedBefore = this.formatSummaryDate(normalizedFilters.lastUsedBefore);
        if(lastUsedAfter && lastUsedBefore) {
            summary.push("laatst online tussen " + lastUsedAfter + " en " + lastUsedBefore);
        } else if(lastUsedAfter) {
            summary.push("laatst online vanaf " + lastUsedAfter);
        } else if(lastUsedBefore) {
            summary.push("laatst online voor " + lastUsedBefore);
        }

        if(summary.length === 1) {
            summary.push("geen filters actief");
        }

        return summary.join(", ");
    }

    renderDateValue(date) {
        if(!date || !moment(date).isValid()) {
            return "-";
        }
        return <DateFormatter date={ date }/>;
    }

    renderInactiveDays(inactiveDays) {
        if(inactiveDays === null || inactiveDays === undefined || inactiveDays === "") {
            return "-";
        }
        const inactiveDaysInt = parseInt(inactiveDays, 10);
        if(Number.isNaN(inactiveDaysInt)) {
            return "-";
        }
        return inactiveDaysInt + " " + (inactiveDaysInt === 1 ? "dag" : "dagen");
    }

    renderInactiveFilterDropdown() {
        const {
            filters,
            loading
        } = this.state;
        const inactiveFilterActive = this.hasAppliedFilterValues(["inactiveForValue"]);
        const presetValues = [
            { value: "3", unit: "months", label: "3 maanden" },
            { value: "6", unit: "months", label: "6 maanden" },
            { value: "12", unit: "months", label: "12 maanden" }
        ];

        return (
            <Dropdown className="align-self-center">
                <Dropdown.Toggle
                    variant={ "tabbar-filter" + (inactiveFilterActive ? " active" : "") }
                    id="filter-inactive"
                >
                    { this.getInactiveToggleLabel() }
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-0">
                    <div className="px-3 pt-3 pb-2">
                        <small className="text-uppercase text-muted">Snel kiezen</small>
                    </div>
                    { presetValues.map((presetValue) => {
                        const selected = filters.inactiveForValue === presetValue.value && filters.inactiveForUnit === presetValue.unit;
                        return (
                            <Dropdown.Item
                                key={ presetValue.label }
                                onClick={ () => this.applyFilterPatch({ inactiveForValue: presetValue.value, inactiveForUnit: presetValue.unit }) }
                                className="d-flex justify-content-between align-items-center"
                            >
                                <span>{ presetValue.label }</span>
                                { selected && (
                                    <i className="fas fa-check text-primary ml-3"/>
                                )}
                            </Dropdown.Item>
                        );
                    })}
                    <div className="dropdown-divider mb-0"/>
                    <div className="px-3 py-3" style={{ minWidth: "320px" }}>
                        <small className="text-uppercase text-muted d-block mb-2">Aangepast</small>
                        <div className="d-flex">
                            <input
                                type="number"
                                min="1"
                                className="form-control"
                                id="inactiveForValue"
                                name="inactiveForValue"
                                value={ filters.inactiveForValue }
                                onChange={ this.handleFilterChange }
                                placeholder="Bijv. 3"
                            />
                            <select
                                className="custom-select ml-2"
                                id="inactiveForUnit"
                                name="inactiveForUnit"
                                value={ filters.inactiveForUnit }
                                onChange={ this.handleFilterChange }
                                style={{ maxWidth: "140px" }}
                            >
                                <option value="days">dagen</option>
                                <option value="months">maanden</option>
                            </select>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <Button
                                type="button"
                                variant="link"
                                className="px-0 text-muted"
                                onClick={ () => this.clearFilterGroup(["inactiveForValue", "inactiveForUnit"]) }
                                disabled={ loading }
                            >
                                Wissen
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={ this.applyFilters }
                                disabled={ loading }
                            >
                                Toepassen
                            </Button>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    renderDateRangeFilterDropdown({ id, title, afterKey, beforeKey }) {
        const {
            filters,
            loading
        } = this.state;
        const filterKeys = [afterKey, beforeKey];
        const active = this.hasAppliedFilterValues(filterKeys);

        return (
            <Dropdown className="align-self-center">
                <Dropdown.Toggle
                    variant={ "tabbar-filter" + (active ? " active" : "") }
                    id={ id }
                >
                    { this.getRangeToggleLabel(title, filterKeys) }
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-0">
                    <div className="px-3 py-3" style={{ minWidth: "320px" }}>
                        <div className="form-group mb-3">
                            <label htmlFor={ afterKey } className="mb-1">Van</label>
                            <input
                                type="date"
                                className="form-control"
                                id={ afterKey }
                                name={ afterKey }
                                value={ filters[afterKey] }
                                onChange={ this.handleFilterChange }
                            />
                        </div>
                        <div className="form-group mb-0">
                            <label htmlFor={ beforeKey } className="mb-1">Tot</label>
                            <input
                                type="date"
                                className="form-control"
                                id={ beforeKey }
                                name={ beforeKey }
                                value={ filters[beforeKey] }
                                onChange={ this.handleFilterChange }
                            />
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <Button
                                type="button"
                                variant="link"
                                className="px-0 text-muted"
                                onClick={ () => this.clearFilterGroup(filterKeys) }
                                disabled={ loading }
                            >
                                Wissen
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={ this.applyFilters }
                                disabled={ loading }
                            >
                                Toepassen
                            </Button>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    render() {
        const {
            shopUsers,
            error,
            exportError,
            loading,
            exportLoading
        } = this.state;
        const baseUrl = "/shop/" + this.context.currentShop.codeName;
        const entitlementCourses = this.context.currentShop.entitlementCourses;
        const filtersDirty = this.hasPendingFilterChanges();
        const hasAppliedFilters = this.hasActiveFilters(this.state.appliedFilters);
        const colSpan = entitlementCourses ? 7 : 6;

        return (
            <React.Fragment>
                <Helmet title="Gebruikers"/>
                <Title
                    preTitle="Overzicht"
                    noBottom
                    preChildren={
                        <div className="float-right mt-2">
                            <OverlayTrigger overlay={
                                <Tooltip id="shop-users-export">Download CSV export</Tooltip>
                            }>
                                <Button
                                    variant="secondary"
                                    onClick={ this.exportShopUsers }
                                    disabled={ loading || exportLoading || shopUsers === null }
                                >
                                    { exportLoading ? (
                                        <Spinner animation="border" size="sm"/>
                                    ) : (
                                        <i className="fas fa-fw fa-file-csv"/>
                                    )}
                                </Button>
                            </OverlayTrigger>
                        </div>
                    }
                >
                    Gebruikers
                    <small>
                        <TagPill className="ml-2">{ shopUsers ? shopUsers.length : 0 }</TagPill>
                    </small>
                </Title>

                <TabBar noBottom>
                    { this.renderInactiveFilterDropdown() }
                    { entitlementCourses && this.renderDateRangeFilterDropdown({
                        id: "filter-last-course-purchase",
                        title: "Laatste cursusaankoop",
                        afterKey: "lastCoursePurchaseAfter",
                        beforeKey: "lastCoursePurchaseBefore"
                    }) }
                    { this.renderDateRangeFilterDropdown({
                        id: "filter-last-used",
                        title: "Laatst online",
                        afterKey: "lastUsedAfter",
                        beforeKey: "lastUsedBefore"
                    }) }
                    { hasAppliedFilters && (
                        <Button
                            type="button"
                            variant="link"
                            className="align-self-center ml-2 text-muted"
                            onClick={ this.clearFilters }
                            disabled={ loading }
                        >
                            Filter wissen
                        </Button>
                    )}
                </TabBar>

                <div className="mt-3 mb-3">
                    <p className="mb-0 font-weight-bold">{ this.getAppliedFilterSummary() }</p>
                    { filtersDirty && (
                        <p className="mb-0 mt-1 text-muted small">
                            Niet-toegepaste wijzigingen klaar. Klik op Toepassen in een filter om de tabel en export te verversen.
                        </p>
                    )}
                </div>

                { exportError && (
                    <Alert variant="danger" className="mb-3">{ exportError }</Alert>
                )}

                <Table size="sm" className="results" hover={ shopUsers && shopUsers.length > 0 }>
                    <thead className="thead-light">
                    <tr>
                        <th className="text-center">#</th>
                        <th className="text-left">Naam</th>
                        <th className="text-left">Email</th>
                        <th className="text-left">Laatst online</th>
                        <th className="text-left">Inactief</th>
                        { entitlementCourses && (
                            <th className="text-left">Laatste cursusaankoop</th>
                        )}
                        <th className="text-left">Geregistreerd op</th>
                    </tr>
                    </thead>
                    <tbody>
                    { error ? (
                        <tr>
                            <td colSpan={ colSpan }>
                                <Alert variant="danger" className="mb-0">{ error }</Alert>
                            </td>
                        </tr>
                    ) : shopUsers === null ? (
                        <tr>
                            <td colSpan={ colSpan }>
                                <Loading/>
                            </td>
                        </tr>
                    ) : shopUsers.length === 0 ? (
                        <tr>
                            <td colSpan={ colSpan } className="text-center">
                                <h1><i className="fas fa-shopping-basket"/></h1>
                                <h4>Geen accounts gevonden</h4>
                                <p>Geen accounts gevonden voor deze filters.</p>
                            </td>
                        </tr>
                    ) : shopUsers.map((shopUser) => {
                        const url = baseUrl + "/shop-user/" + shopUser.id;
                        return (
                            <tr key={ shopUser.id } className="table-clickable-row">
                                <th scope="row" className="align-middle text-center">
                                    <Link to={ url }>
                                        { shopUser.id }
                                    </Link>
                                </th>
                                <td className="align-middle">
                                    <Link to={ url }>
                                        { this.getShopUserName(shopUser) }
                                    </Link>
                                </td>
                                <td className="align-middle">
                                    <Link to={ url }>
                                        { shopUser.email }
                                    </Link>
                                </td>
                                <td className="align-middle">
                                    <Link to={ url }>
                                        { this.renderDateValue(shopUser.lastUsedDate) }
                                    </Link>
                                </td>
                                <td className="align-middle">
                                    <Link to={ url }>
                                        { this.renderInactiveDays(shopUser.inactiveDays) }
                                    </Link>
                                </td>
                                { entitlementCourses && (
                                    <td className="align-middle">
                                        <Link to={ url }>
                                            { this.renderDateValue(shopUser.lastCoursePurchaseDate) }
                                        </Link>
                                    </td>
                                )}
                                <td className="align-middle">
                                    <Link to={ url }>
                                        { this.renderDateValue(shopUser.registerDate) }
                                    </Link>
                                </td>
                            </tr>
                        )
                    })}
                    </tbody>
                </Table>
            </React.Fragment>
        )
    }
}
ShopUsers.contextType = ShopsContext;

export default ShopUsers;
